export type Timeframe = 'weekly' | 'monthly'

export type PacingStatus = 'Ahead' | 'On Track' | 'Behind' | 'At Risk'

export interface DailyDataPoint {
  day: number
  label: string
  softPullActual: number
  submittedActual: number
  softPullIdeal: number
  submittedIdeal: number
}

export interface Loan {
  id: string
  borrowerName: string
  amount: number
  type: 'softPull' | 'submitted'
  timestamp: string
}

export interface DailyLoanData {
  day: number
  loans: Loan[]
}

export interface HistoricalPeriod {
  id: string
  label: string
  startDate: string
  softPullActual: number
  softPullGoal: number
  submittedActual: number
  submittedGoal: number
}

export interface GoalMetric {
  name: string
  current: number
  goal: number
  unit: string
}

export interface LoanOfficer {
  id: string
  name: string
  completionDate: string
  achievementPct: number
}

export interface StreakUser {
  id: string
  name: string
  streakCount: number
}

export interface PeriodData {
  label: string
  totalDays: number
  daysPassed: number
  daysRemaining: number
  softPull: GoalMetric
  submitted: GoalMetric
  dailyData: DailyDataPoint[]
  historicalPeriods: HistoricalPeriod[]
  loanOfficers: LoanOfficer[]
  streakUsers: StreakUser[]
  dailyLoans: DailyLoanData[]
}

export function getPacingStatus(
  current: number,
  expected: number,
): PacingStatus {
  if (expected === 0) return 'On Track'
  const ratio = current / expected
  if (ratio >= 1.05) return 'Ahead'
  if (ratio >= 1.0) return 'On Track'
  if (ratio >= 0.8) return 'Behind'
  return 'At Risk'
}

export function getPacingColor(status: PacingStatus): {
  bg: string
  text: string
  ring: string
  fill: string
} {
  switch (status) {
    case 'Ahead':
      return {
        bg: 'bg-emerald-500/10',
        text: 'text-emerald-400',
        ring: 'ring-emerald-500/30',
        fill: '#34d399',
      }
    case 'On Track':
      return {
        bg: 'bg-green-500/10',
        text: 'text-green-400',
        ring: 'ring-green-500/30',
        fill: '#4ade80',
      }
    case 'Behind':
      return {
        bg: 'bg-amber-500/10',
        text: 'text-amber-400',
        ring: 'ring-amber-500/30',
        fill: '#fbbf24',
      }
    case 'At Risk':
      return {
        bg: 'bg-red-500/10',
        text: 'text-red-400',
        ring: 'ring-red-500/30',
        fill: '#f87171',
      }
  }
}

export function getAchievementStatus(percentage: number): PacingStatus {
  if (percentage >= 105) return 'Ahead'
  if (percentage >= 100) return 'On Track'
  if (percentage >= 80) return 'Behind'
  return 'At Risk'
}

export function getProjectedTotal(
  current: number,
  daysPassed: number,
  totalDays: number,
): number {
  if (daysPassed === 0) return 0
  const dailyRate = current / daysPassed
  return Math.round(dailyRate * totalDays)
}

function generateWeeklyDailyData(): DailyDataPoint[] {
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const softPullGoal = 35
  const submittedGoal = 20
  const softPullDaily = softPullGoal / 7
  const submittedDaily = submittedGoal / 7

  let softPullCum = 0
  let submittedCum = 0
  const currentDay = 4

  return dayLabels.map((label, i) => {
    const day = i + 1
    const softPullIdeal = Math.round(softPullDaily * day * 10) / 10
    const submittedIdeal = Math.round(submittedDaily * day * 10) / 10

    if (day <= currentDay) {
      const softPullVariance = [6, 4, 7, 5, 0, 0, 0]
      const submittedVariance = [3, 2, 4, 3, 0, 0, 0]
      softPullCum += softPullVariance[i]
      submittedCum += submittedVariance[i]
    }

    return {
      day,
      label,
      softPullActual: day <= currentDay ? softPullCum : 0,
      submittedActual: day <= currentDay ? submittedCum : 0,
      softPullIdeal: Math.round(softPullIdeal),
      submittedIdeal: Math.round(submittedIdeal),
    }
  })
}

function generateWeeklyLoanData(): DailyLoanData[] {
  const borrowerNames = [
    'John Smith',
    'Sarah Johnson',
    'Michael Brown',
    'Emily Davis',
    'David Wilson',
    'Jessica Martinez',
    'James Anderson',
    'Jennifer Taylor',
    'Robert Thomas',
    'Linda Garcia',
    'William Rodriguez',
    'Patricia Lee',
  ]

  const softPullCounts = [6, 4, 7, 5, 0, 0, 0]
  const submittedCounts = [3, 2, 4, 3, 0, 0, 0]

  return softPullCounts.map((spCount, dayIndex) => {
    const day = dayIndex + 1
    const subCount = submittedCounts[dayIndex]
    const loans: Loan[] = []

    // Generate soft pull loans
    for (let i = 0; i < spCount; i++) {
      const hour = 9 + Math.floor(Math.random() * 8)
      const minute = Math.floor(Math.random() * 60)
      loans.push({
        id: `sp-${day}-${i}`,
        borrowerName:
          borrowerNames[Math.floor(Math.random() * borrowerNames.length)],
        amount: Math.round((150000 + Math.random() * 350000) / 1000) * 1000,
        type: 'softPull',
        timestamp: `2026-02-${17 + dayIndex}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`,
      })
    }

    // Generate submitted loans
    for (let i = 0; i < subCount; i++) {
      const hour = 10 + Math.floor(Math.random() * 7)
      const minute = Math.floor(Math.random() * 60)
      loans.push({
        id: `sub-${day}-${i}`,
        borrowerName:
          borrowerNames[Math.floor(Math.random() * borrowerNames.length)],
        amount: Math.round((200000 + Math.random() * 400000) / 1000) * 1000,
        type: 'submitted',
        timestamp: `2026-02-${17 + dayIndex}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`,
      })
    }

    // Sort by timestamp
    loans.sort((a, b) => a.timestamp.localeCompare(b.timestamp))

    return { day, loans }
  })
}

function generateMonthlyDailyData(): DailyDataPoint[] {
  const totalDays = 28
  const currentDay = 18
  const softPullGoal = 150
  const submittedGoal = 85
  const softPullDaily = softPullGoal / totalDays
  const submittedDaily = submittedGoal / totalDays

  let softPullCum = 0
  let submittedCum = 0

  const points: DailyDataPoint[] = []

  for (let i = 0; i < totalDays; i++) {
    const day = i + 1
    const softPullIdeal = Math.round(softPullDaily * day)
    const submittedIdeal = Math.round(submittedDaily * day)

    if (day <= currentDay) {
      const baseSP = softPullDaily + Math.sin(i * 0.7) * 2
      const baseSub = submittedDaily + Math.cos(i * 0.5) * 1.5
      softPullCum += Math.max(0, Math.round(baseSP))
      submittedCum += Math.max(0, Math.round(baseSub))
    }

    points.push({
      day,
      label: `Day ${day}`,
      softPullActual: day <= currentDay ? softPullCum : 0,
      submittedActual: day <= currentDay ? submittedCum : 0,
      softPullIdeal,
      submittedIdeal,
    })
  }

  return points
}

const weeklyHistorical: HistoricalPeriod[] = [
  {
    id: 'w1',
    label: 'Feb 10 – Feb 16',
    startDate: '2026-02-10',
    softPullActual: 38,
    softPullGoal: 35,
    submittedActual: 22,
    submittedGoal: 20,
  },
  {
    id: 'w2',
    label: 'Feb 3 – Feb 9',
    startDate: '2026-02-03',
    softPullActual: 33,
    softPullGoal: 35,
    submittedActual: 19,
    submittedGoal: 20,
  },
  {
    id: 'w3',
    label: 'Jan 27 – Feb 2',
    startDate: '2026-01-27',
    softPullActual: 41,
    softPullGoal: 35,
    submittedActual: 24,
    submittedGoal: 20,
  },
  {
    id: 'w4',
    label: 'Jan 20 – Jan 26',
    startDate: '2026-01-20',
    softPullActual: 29,
    softPullGoal: 35,
    submittedActual: 16,
    submittedGoal: 20,
  },
  {
    id: 'w5',
    label: 'Jan 13 – Jan 19',
    startDate: '2026-01-13',
    softPullActual: 36,
    softPullGoal: 35,
    submittedActual: 21,
    submittedGoal: 20,
  },
  {
    id: 'w6',
    label: 'Jan 6 – Jan 12',
    startDate: '2026-01-06',
    softPullActual: 30,
    softPullGoal: 35,
    submittedActual: 17,
    submittedGoal: 20,
  },
  {
    id: 'w7',
    label: 'Dec 30 – Jan 5',
    startDate: '2025-12-30',
    softPullActual: 25,
    softPullGoal: 35,
    submittedActual: 14,
    submittedGoal: 20,
  },
  {
    id: 'w8',
    label: 'Dec 23 – Dec 29',
    startDate: '2025-12-23',
    softPullActual: 37,
    softPullGoal: 35,
    submittedActual: 23,
    submittedGoal: 20,
  },
]

const monthlyHistorical: HistoricalPeriod[] = [
  {
    id: 'm1',
    label: 'January 2026',
    startDate: '2026-01-01',
    softPullActual: 158,
    softPullGoal: 150,
    submittedActual: 88,
    submittedGoal: 85,
  },
  {
    id: 'm2',
    label: 'December 2025',
    startDate: '2025-12-01',
    softPullActual: 132,
    softPullGoal: 150,
    submittedActual: 72,
    submittedGoal: 85,
  },
  {
    id: 'm3',
    label: 'November 2025',
    startDate: '2025-11-01',
    softPullActual: 161,
    softPullGoal: 150,
    submittedActual: 91,
    submittedGoal: 85,
  },
  {
    id: 'm4',
    label: 'October 2025',
    startDate: '2025-10-01',
    softPullActual: 145,
    softPullGoal: 150,
    submittedActual: 80,
    submittedGoal: 85,
  },
  {
    id: 'm5',
    label: 'September 2025',
    startDate: '2025-09-01',
    softPullActual: 138,
    softPullGoal: 150,
    submittedActual: 76,
    submittedGoal: 85,
  },
  {
    id: 'm6',
    label: 'August 2025',
    startDate: '2025-08-01',
    softPullActual: 155,
    softPullGoal: 150,
    submittedActual: 89,
    submittedGoal: 85,
  },
]

export function getPeriodData(
  timeframe: Timeframe,
  periodOffset: number,
): PeriodData {
  if (timeframe === 'weekly') {
    const dailyData = generateWeeklyDailyData()
    const dailyLoans = generateWeeklyLoanData()
    const currentDay = 4
    const totalDays = 7
    const softPullCurrent = 22
    const submittedCurrent = 12

    const weeklyLoanOfficers: LoanOfficer[] = [
      {
        id: 'lo1',
        name: 'Sarah Chen',
        completionDate: '2026-02-19',
        achievementPct: 112,
      },
      {
        id: 'lo2',
        name: 'Michael Torres',
        completionDate: '2026-02-19',
        achievementPct: 105,
      },
      {
        id: 'lo3',
        name: 'Jessica Park',
        completionDate: '2026-02-18',
        achievementPct: 108,
      },
      {
        id: 'lo4',
        name: 'David Kim',
        completionDate: '2026-02-17',
        achievementPct: 103,
      },
    ]

    const weeklyStreakUsers: StreakUser[] = [
      { id: 'su1', name: 'Sarah Chen', streakCount: 8 },
      { id: 'su2', name: 'Jessica Park', streakCount: 6 },
      { id: 'su3', name: 'Michael Torres', streakCount: 5 },
      { id: 'su4', name: 'David Kim', streakCount: 3 },
    ]

    return {
      label:
        periodOffset === 0
          ? 'This Week (Feb 17 – Feb 23)'
          : `Week of Feb ${17 - periodOffset * 7}`,
      totalDays,
      daysPassed: periodOffset === 0 ? currentDay : totalDays,
      daysRemaining: periodOffset === 0 ? totalDays - currentDay : 0,
      softPull: {
        name: 'Soft Pull',
        current: softPullCurrent,
        goal: 35,
        unit: 'pulls',
      },
      submitted: {
        name: 'Submitted',
        current: submittedCurrent,
        goal: 20,
        unit: 'submissions',
      },
      dailyData,
      historicalPeriods: weeklyHistorical,
      loanOfficers: weeklyLoanOfficers,
      streakUsers: weeklyStreakUsers,
      dailyLoans,
    }
  }

  const dailyData = generateMonthlyDailyData()
  const currentDay = 18
  const totalDays = 28
  const softPullCurrent = 102
  const submittedCurrent = 52

  const monthlyLoanOfficers: LoanOfficer[] = [
    {
      id: 'lo1',
      name: 'Sarah Chen',
      completionDate: '2026-02-15',
      achievementPct: 118,
    },
    {
      id: 'lo2',
      name: 'Michael Torres',
      completionDate: '2026-02-14',
      achievementPct: 107,
    },
    {
      id: 'lo3',
      name: 'Jessica Park',
      completionDate: '2026-02-12',
      achievementPct: 112,
    },
    {
      id: 'lo4',
      name: 'David Kim',
      completionDate: '2026-02-10',
      achievementPct: 104,
    },
    {
      id: 'lo5',
      name: 'Amanda Rodriguez',
      completionDate: '2026-02-08',
      achievementPct: 101,
    },
  ]

  const monthlyStreakUsers: StreakUser[] = [
    { id: 'su1', name: 'Sarah Chen', streakCount: 6 },
    { id: 'su2', name: 'Jessica Park', streakCount: 4 },
    { id: 'su3', name: 'Amanda Rodriguez', streakCount: 3 },
    { id: 'su4', name: 'Michael Torres', streakCount: 2 },
  ]

  return {
    label: periodOffset === 0 ? 'February 2026' : 'January 2026',
    totalDays,
    daysPassed: periodOffset === 0 ? currentDay : totalDays,
    daysRemaining: periodOffset === 0 ? totalDays - currentDay : 0,
    softPull: {
      name: 'Soft Pull',
      current: softPullCurrent,
      goal: 150,
      unit: 'pulls',
    },
    submitted: {
      name: 'Submitted',
      current: submittedCurrent,
      goal: 85,
      unit: 'submissions',
    },
    dailyData,
    historicalPeriods: monthlyHistorical,
    loanOfficers: monthlyLoanOfficers,
    streakUsers: monthlyStreakUsers,
    dailyLoans: [],
  }
}
